<script setup>
import { ref, computed } from "vue";
import { useSchoolStore } from "@/Store/schoolStore";
import { nameRules, emailRules, contactRules } from "@/utils/validationRule";

const schoolStore = useSchoolStore();
const editableDetails = ref({});
const isDialogOpen = ref(false);
const editingSection = ref("");
const dialogTitle = ref("");
const valid = ref(false);

// Fetch school details when component mounts
schoolStore.fetchSchoolDetails();

const sectionTitles = {
  school: "Edit School Details",
  signature: "Edit Principal's Signature",
  blur: "Edit Blur Logo",
};

const openEditDialog = (section) => {
  editingSection.value = section;
  dialogTitle.value = sectionTitles[section] || "Edit Details";
  editableDetails.value = { ...schoolStore.details };
  isDialogOpen.value = true;
};

const onFileChange = (event, type) => {
  const file = event?.target?.files?.[0];
  if (file) {
    editableDetails.value[type] = file;
    // Create preview URL
    editableDetails.value[`${type}_preview`] = URL.createObjectURL(file);
  }
};

const saveDetails = async () => {
  if (!valid.value) return;

  const formData = new FormData();
  const fileKeys = ["logo", "logo_blur", "principal_sign"];
  const textKeys = ["name", "address", "contact", "principal_name"];

  // Add changed fields to formData
  fileKeys.forEach((key) => {
    const value = editableDetails.value[key];
    if (value instanceof File) {
      formData.append(key, value);
    }
  });

  textKeys.forEach((key) => {
    const value = editableDetails.value[key];
    if (value !== undefined && value !== schoolStore.details[key]) {
      formData.append(key, value);
    }
  });

  const success = await schoolStore.updateSchoolDetails(formData);
  if (success) {
    isDialogOpen.value = false;
  }
};
</script>

<template>
  <v-container>
    <!-- School Details -->
    <v-row justify="center" align="start" class="mb-8">
      <v-col cols="12" sm="8" md="7">
        <v-card class="pa-6 professional-container" outlined>
          <v-row class="mb-0">
            <v-col class="pa-0 d-flex justify-space-between align-start flex-wrap">
              <div class="title-wrapper">
                <v-card-title :class="['school-title', schoolStore.isLongName ? 'small-name' : '']">
                  {{ schoolStore.details.name }}
                </v-card-title>
              </div>

              <v-btn class="icon-hover-btn ml-2" icon small @click="openEditDialog('school')">
                <v-icon color="secondary">mdi-pencil</v-icon>
              </v-btn>
            </v-col>
          </v-row>


          <v-row>
            <v-col cols="12" sm="4" md="5" class="d-flex justify-center align-center">
              <div class="image-container">
                <v-img :src="editableDetails.logo_preview || schoolStore.details.logo" alt="School Logo" contain
                  class="school-logo" />
              </div>
            </v-col>

            <v-col cols="12" sm="8" md="7">
              <div class="info-box">
                <v-icon color="blue darken-1" class="mr-3">mdi-map-marker</v-icon>
                <div class="info-content">
                  <span class="info-title">Address:</span>
                  <span class="info-value">{{ schoolStore.details.address }}</span>
                </div>
              </div>

              <div class="info-box">
                <v-icon color="blue darken-1" class="mr-3">mdi-account-tie</v-icon>
                <div class="info-content">
                  <span class="info-title">Principal:</span>
                  <span class="info-value">{{ schoolStore.details.principal_name }}</span>
                </div>
              </div>

              <div class="info-box">
                <v-icon color="blue darken-1" class="mr-3">mdi-phone</v-icon>
                <div class="info-content">
                  <span class="info-title">Contact:</span>
                  <span class="info-value">{{ schoolStore.details.contact }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Signature & Blur Logo -->
      <v-col cols="12" sm="8" md="4">
        <v-card class="pa-3 mb-4 signature-card" outlined>
          <v-row class="align-center justify-space-between">
            <v-card-title class="font-weight-bold">Principal's Signature</v-card-title>
            <v-btn class="icon-hover-btn mr-2" icon small @click="openEditDialog('signature')">
              <v-icon color="secondary">mdi-pencil</v-icon>
            </v-btn>
          </v-row>
          <div class="image-container mx-auto">
            <v-img :src="editableDetails.principal_sign_preview || schoolStore.details.principal_sign" contain
              class="signature-logo" />
          </div>
        </v-card>

        <v-card class="pa-3 signature-card" outlined>
          <v-row class="align-center justify-space-between">
            <v-card-title class="font-weight-bold">Blur Logo</v-card-title>
            <v-btn class="icon-hover-btn mr-2" icon small @click="openEditDialog('blur')">
              <v-icon color="secondary">mdi-pencil</v-icon>
            </v-btn>
          </v-row>
          <div class="image-container mx-auto">
            <v-img :src="editableDetails.logo_blur_preview || schoolStore.details.logo_blur" contain
              class="signature-logo" />
          </div>
        </v-card>
      </v-col>
    </v-row>
    <!-- Edit Dialog -->
    <v-dialog v-model="isDialogOpen" max-width="800px">
      <v-card>
        <v-card-title class="headline">
          <span class="modal-title">{{ dialogTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="isDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveDetails" v-model="valid">
            <v-text-field v-if="editingSection === 'school'" v-model="editableDetails.name" label="School Name"
              outlined />
            <v-text-field v-if="editingSection === 'school'" v-model="editableDetails.address" label="School Address"
              outlined />
            <v-text-field v-if="editingSection === 'school'" v-model="editableDetails.principal_name"
              label="Principal Name" outlined />
            <v-text-field v-if="editingSection === 'school'" v-model="editableDetails.contact" label="Contact Number"
              outlined />
            <v-file-input v-if="editingSection === 'school'" label="Upload School Logo" accept="image/*" outlined
              @change="(e) => onFileChange(e, 'logo')" />
            <v-file-input v-if="editingSection === 'signature'" label="Upload Principal's Signature" accept="image/*"
              outlined @change="(e) => onFileChange(e, 'principal_sign')" />
            <v-file-input v-if="editingSection === 'blur'" label="Upload Blur Logo" accept="image/*" outlined
              @change="(e) => onFileChange(e, 'logo_blur')" />
          </v-form>
          <v-row class="justify-end mt-4">
            <v-btn color="grey" @click="isDialogOpen = false" class="mr-4">Cancel</v-btn>
            <v-btn color="primary" @click="saveDetails">Save</v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.image-container {
  max-width: 150px;
  max-height: 150px;
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 50%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: white;
  margin-top: 20px;
}

.school-logo,
.signature-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.info-box {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.info-content {
  display: flex;
  flex-direction: column;
}

/* .school-title {
    font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #424242;
  display: block;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  display: block;
  margin-bottom: 4px;
} */

.school-title {
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #424242;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin-bottom: 4px;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
}

.small-name {
  font-size: 1.2rem;
  line-height: 1.3;
}

.title-wrapper {
  flex: 1;
  min-width: 0;
}

.dark-theme .school-title {
  color: #fff;
}

.info-title {
  font-size: 17px;
  font-weight: 700;
  color: #424242;
  margin-bottom: 4px;
}

.dark-theme .info-title {
  color: #367b9b;
}

.info-value {
  font-size: 20px;
  font-weight: 700;
  /* text-transform: uppercase; */
  color: #424242;
  display: block;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.dark-theme .info-value {
  color: #fff;
}

.professional-container {
  background: linear-gradient(135deg, #51ec5934, #e3e9e91c);
  padding: 20px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}
</style>
